import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { Skeleton } from "antd";
import * as _ from "lodash";
import { useDocumentTitle } from "@uidotdev/usehooks";

import { getEnvVariables } from "@/constants/getEnvVariables";

import { NavbarNuevo } from "../pages/home/components/MenuNuevo";
import { ErrorBoundary } from "../pages/home/components/ErrorBoundary";
import { Footer } from "@/layouts/Footer";

import { useCheckToken } from "@/hooks/useAuth";
import { useSesionExpirada } from "@/hooks/useSesionExpirada";

import { Home } from "@/pages/home/Home";
import { ProductsAvailablePage } from "@/pages/products-available/ProductsAvailablePage";
import { StorePage } from "@/pages/store/StorePage";
import { ProductPage } from "@/pages/product/ProductPage";
import { CartPage } from "@/pages/cart/CartPage";
import { PayDetailPage } from "@/pages/pay-detail/PayDetailPage";
import { AboutUsPage } from "@/pages/about-us/AboutUsPage";
import { ContactPage } from "@/pages/contact/ContactPage";
import { PrivacyPage } from "../pages/privacy/PrivacyPage2";
import { OrderPage } from "../pages/order/OrderPage";
import { Login } from "../pages/auth/Login";
import { SignIn } from "../pages/auth/SignIn";
import { RecoveryPasswordPage } from "../pages/auth/RecoveryPasswordPage";
import { ForgotPassword } from "../pages/auth/ForgotPassword";
import { MyOrdersPage } from "../pages/MyOrders/MyOrdersPage";
import { MyOrdersCompanyPage } from "../pages/MyOrders/MyOrdersCompanyPage";
import { useMachine } from "@/_zustand/machine";
import { Stores } from "../pages/store/Stores";
import { StoresCompany } from "../pages/store/StoresCompany";
import { Featured } from "../pages/featured-products/Featured";
import { ProfilePage } from "../pages/profile/ProfilePage";
import { BestSeller } from "../pages/best-sellers/BestSeller";
import { ContactCompanyPage } from "../pages/contact/ContactCompanyPage";
import { MercadoPagoReturnPage } from "../pages/mp-return/MercadoPagoReturnPage";
import { AppleCallbackPage } from "../pages/auth/AppleCallbackPage";

/**
 * Al llegar a / o /home se considera que el usuario quiere el flujo global.
 * Limpiamos companyHome para salir del contexto de empresa.
 */
const HomeRedirect = () => {
  localStorage.removeItem("companyHome");
  return <Home />;
};

const Routers = () => {
  const auth = useCheckToken();
  useSesionExpirada();
  const { pathname } = useLocation();
  const { APP_ENV } = getEnvVariables();
  const { clearMachine } = useMachine();

  // Para cualquier otro APP_ENV mantenemos el título actual del documento
  // (equivalente a no llamar el hook, evitando forzar document.title a "undefined").
  const documentTitle =
    APP_ENV == "production"
      ? "OpenMatic - WebStore"
      : APP_ENV == "development"
        ? "OpenMatic - TestWeb"
        : document.title;
  useDocumentTitle(documentTitle);

  useEffect(() => {
    let paths = [
      "/pay-detail",
      "/mp-return",
      "/company/mp-return",
      "/login",
      "/register",
      "/forgot-password",
      "/recovery-password",
      "/apple-callback",
    ];
    if (_.includes(paths, pathname)) {
      return;
    }

    localStorage.setItem("lastPatch", pathname);

    if (pathname.includes("store")) {
      let lastPath = localStorage.getItem("lastPatch");

      if (lastPath.includes("cart")) {
        return;
      } else {
        clearMachine();
      }
    }

    const companyRouteRegex = /^\/company\/(\d+)/;
    const companyMatch = companyRouteRegex.exec(pathname);
    if (companyMatch) {
      localStorage.setItem("companyHome", `/company/${companyMatch[1]}`);
    } else if (pathname === "/" || pathname === "/home") {
      localStorage.removeItem("companyHome");
    }
  }, [pathname, clearMachine]);

  return (
    <>
      {/* <Header /> */}
      <ErrorBoundary name="navbar" title="Algo salió mal en el buscador">
        <NavbarNuevo />
      </ErrorBoundary>
      <ErrorBoundary
        name="routes"
        title="Algo salió mal"
        description="Ocurrió un problema al mostrar esta página."
        key={pathname}
      >
        {/* auth.isLoading (no isFetching): con sesión de usuario, useCheckToken
            sigue refetcheando el token invitado cada 35 min / en cada focus
            (CA-1/CA-7), pero eso ya no debe tapar la app entera con el
            skeleton — isFetching parpadeaba en cada refetch en segundo plano,
            isLoading solo cubre el primer arranque. */}
        <Skeleton loading={auth.isLoading}>
          <Routes>
            {/* Global Flow */}
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/home" element={<HomeRedirect />} />
            <Route
              path="/products-available"
              element={<ProductsAvailablePage />}
            />
            <Route
              path="/store/:empresaID/:generoID?/:marcaID?"
              element={<StorePage />}
            />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/pay-detail" element={<PayDetailPage />} />
            <Route path="/mp-return" element={<MercadoPagoReturnPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/contact/:empresa_id?" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/order" element={<OrderPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/apple-callback" element={<AppleCallbackPage />} />
            <Route path="/register" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/recovery-password"
              element={<RecoveryPasswordPage />}
            />
            <Route path="/orders" element={<MyOrdersPage />} />
            <Route path="/stores/:tiendaId" element={<Stores />} />
            <Route path="/featured-products" element={<Featured />} />
            <Route path="/best-seller" element={<BestSeller />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/:tiendaId" element={<Stores />} />

            {/* Company Flow (Prefixed with /company) */}
            <Route path="/company/:idEmpresa" element={<StoresCompany />} />
            <Route
              path="/company/stores/:tiendaId"
              element={<StoresCompany />}
            />
            <Route
              path="/company/store/:empresaID/:generoID?/:marcaID?"
              element={<StorePage />}
            />
            <Route path="/company/product" element={<ProductPage />} />
            <Route path="/company/cart" element={<CartPage />} />
            <Route path="/company/pay-detail" element={<PayDetailPage />} />
            <Route
              path="/company/mp-return"
              element={<MercadoPagoReturnPage />}
            />
            <Route path="/company/about-us" element={<AboutUsPage />} />
            <Route
              path="/company/contact/:empresa_id?"
              element={<ContactCompanyPage />}
            />
            <Route path="/company/privacy" element={<PrivacyPage />} />
            <Route path="/company/order" element={<OrderPage />} />
            <Route path="/company/orders" element={<MyOrdersCompanyPage />} />
            <Route path="/company/profile" element={<ProfilePage />} />
          </Routes>
        </Skeleton>
      </ErrorBoundary>
      <Footer />
    </>
  );
};
export default Routers;
